import { Box, LinearProgress } from '@mui/material';
import { makeStyles } from '@mui/styles';
import DatePicker from 'components/date-picker/date-picker';
import TextField from 'components/text-field';
import SelectField from 'components/select-field';
import React, { FC, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import {
  OrderMortgageType,
  PayOffInformationform,
} from 'views/orders/mortgage-payoffs/services/types.d';

import { updateMortgageInformation } from '../../../services/mutations';
import { getOrderProperties } from '../../../services/queries';
import { useTranslation } from 'react-i18next';
import NumberField from 'components/number-field';
import { removeSpecialCharacters } from 'utils/helpers';

import {
  derivePayoffFieldRowGroups,
  getPayoffFieldType,
  PAYOFF_INFO_FIELD_LABELS,
  PAYOFF_INFO_FIELDS,
  PayoffInfoFieldKey,
} from './payoff-info-fields';

const useStyles = makeStyles((theme: any) => ({
  row: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(2),
    alignItems: 'flex-end',
    marginBottom: theme.spacing(1.5),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: theme.spacing(1.5),
    },
  },
  field: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  // Reserves the progress bar's height so autosave never shifts the fields.
  progressSlot: {
    height: theme.spacing(0.5),
    marginBottom: theme.spacing(1),
  },
  numeric: {
    '& input': {
      textAlign: 'right',
      fontVariantNumeric: 'tabular-nums',
    },
  },
}));

type PayeOffformProps = {
  currentMortgage: OrderMortgageType | undefined;
};

const PayOffInfo: FC<PayeOffformProps> = ({ currentMortgage }) => {
  const { id } = useParams<{ id: string }>();
  const { control, formState, handleSubmit, reset } = useForm<PayOffInformationform>({
    mode: 'onChange',
  });

  const { t } = useTranslation();
  const classes = useStyles();

  const { data: propertiesData } = useQuery(
    ['order-properties', id],
    () => getOrderProperties({ id }),
    {
      enabled: !!id,
      refetchOnWindowFocus: false,
    }
  );

  const properties = propertiesData?.getOrder.estates || [];

  useEffect(() => {
    reset({
      book: currentMortgage?.book || '',
      certificate_title: currentMortgage?.certificate_title || '',
      deed_date: currentMortgage?.deed_date || '',
      document_number: currentMortgage?.document_number || '',
      instrument_number: currentMortgage?.instrument_number || '',
      page: currentMortgage?.page || '',
      rescission_date: currentMortgage?.rescission_date || '',
      recorded_date: currentMortgage?.recorded_date || '',
      mortgage_amount: currentMortgage?.mortgage_amount || 0,
      loan_number: currentMortgage?.loan_number || '',
      estate_id: currentMortgage?.estate?._id || '',
    });
  }, [currentMortgage, reset]);

  const { mutate: updateOrderMortgageMutation, isLoading: isSaving } = useMutation(updateMortgageInformation);

  const returnNullObjetcs = (propertyString: string) =>
    propertyString !== '' ? propertyString : null;

  const updateMortgageFunc = (data: PayOffInformationform) => {
    updateOrderMortgageMutation({
      id,
      mortgageId: currentMortgage?._id || '',
      data: {
        rescission_date: returnNullObjetcs(data.rescission_date || ''),
        recorded_date: returnNullObjetcs(data.recorded_date || ''),
        page: returnNullObjetcs(data.page || ''),
        instrument_number: returnNullObjetcs(data.instrument_number || ''),
        document_number: returnNullObjetcs(data.document_number || ''),
        deed_date: returnNullObjetcs(data.deed_date || ''),
        certificate_title: returnNullObjetcs(data.certificate_title || ''),
        book: returnNullObjetcs(data.book || ''),
        mortgage_amount: data?.mortgage_amount || 0,
        loan_number: returnNullObjetcs(data.loan_number || ''),
        estate_id: returnNullObjetcs(data.estate_id || ''),
      },
    });
  };

  const handleFieldBlur = (fieldName: keyof PayOffInformationform) => {
    if (formState.dirtyFields[fieldName]) {
      handleSubmit(updateMortgageFunc)();
    }
  };

  const handleSelectChange = (
    onChange: (...event: any[]) => void,
    value: any,
    fieldName?: keyof PayOffInformationform
  ) => {
    onChange(value);
    handleSubmit(updateMortgageFunc)();
  };

  const renderField = (fieldKey: PayoffInfoFieldKey) => {
    const label = t(PAYOFF_INFO_FIELD_LABELS[fieldKey]);

    switch (getPayoffFieldType(fieldKey)) {
      case 'select':
        return (
          <Controller
            control={control}
            name={fieldKey}
            render={({ field }) => (
              <SelectField
                value={(field.value as string | null) || 'not-set'}
                label={label}
                dataKey="_id"
                dataValue="_id"
                dataText="address"
                emptyValue={false}
                data={[
                  { _id: 'not-set', address: t('common:not-set') },
                  ...(properties?.map((property) => ({
                    _id: property._id,
                    address: property.address.address,
                  })) || []),
                ]}
                handleChange={(e) => {
                  const value = e.target.value === 'not-set' ? '' : e.target.value;
                  handleSelectChange(field.onChange, value, fieldKey);
                }}
                handleBlur={field.onBlur}
              />
            )}
          />
        );
      case 'date':
        return (
          <Controller
            control={control}
            name={fieldKey}
            render={({ field }) => (
              <DatePicker
                value={field.value as string | null}
                disableToolbar={false}
                label={label}
                handleBlur={() => handleFieldBlur(fieldKey)}
                onChange={(value) => handleSelectChange(field.onChange, value)}
              />
            )}
          />
        );
      case 'currency':
        return (
          <Controller
            control={control}
            name={fieldKey}
            render={({ field: { ref, ...fieldProps } }) => (
              <NumberField
                {...fieldProps}
                customRef={ref}
                className={classes.numeric}
                label={label}
                thousandSeparator=","
                isNumericString
                prefix="$"
                decimalScale={8}
                onChange={(e) => {
                  fieldProps.onChange(parseFloat(removeSpecialCharacters(e.target.value)));
                }}
                handleBlur={() => handleFieldBlur(fieldKey)}
              />
            )}
          />
        );
      default:
        return (
          <Controller
            control={control}
            name={fieldKey}
            render={({ field: { ref, ...fieldProps } }) => (
              <TextField
                {...fieldProps}
                inputRef={ref}
                label={label}
                handleBlur={() => handleFieldBlur(fieldKey)}
              />
            )}
          />
        );
    }
  };

  return (
    <form aria-busy={isSaving}>
      {/* Autosave-on-blur exception (DESIGN.md §5): signal without disabling so focus and the
          virtual keyboard stay on the next field. */}
      <Box className={classes.progressSlot}>
        {isSaving && <LinearProgress color="secondary" aria-label={`${t('payoffs:saving')}`} />}
      </Box>
      {derivePayoffFieldRowGroups(PAYOFF_INFO_FIELDS).map((row) => (
        <Box key={row.join('-')} className={classes.row}>
          {row.map((fieldKey) => (
            <Box key={fieldKey} className={classes.field}>
              {renderField(fieldKey)}
            </Box>
          ))}
        </Box>
      ))}
    </form>
  );
};

export default PayOffInfo;
